import { useState } from 'react';
import { Heart, Activity, Server, Database, Globe, Wifi, AlertCircle, CheckCircle, XCircle } from 'lucide-react';

function HealthPage() {
  const [selectedSystem, setSelectedSystem] = useState('all');

  // Mock health data
  const healthData = {
    systems: [
      {
        id: 'api',
        name: 'API Server',
        status: 'healthy',
        uptime: '99.9%',
        responseTime: '45ms',
        lastCheck: '2 minutes ago',
        icon: Server
      },
      {
        id: 'database',
        name: 'Database',
        status: 'healthy',
        uptime: '99.8%',
        responseTime: '12ms',
        lastCheck: '1 minute ago',
        icon: Database
      },
      {
        id: 'web',
        name: 'Web Server',
        status: 'warning',
        uptime: '98.5%',
        responseTime: '120ms',
        lastCheck: '3 minutes ago',
        icon: Globe
      },
      {
        id: 'network',
        name: 'Network',
        status: 'healthy',
        uptime: '99.7%',
        responseTime: '8ms',
        lastCheck: '30 seconds ago',
        icon: Wifi
      }
    ],
    metrics: {
      cpu: { current: 45, threshold: 80, status: 'healthy' },
      memory: { current: 67, threshold: 85, status: 'warning' },
      disk: { current: 23, threshold: 90, status: 'healthy' },
      network: { current: 34, threshold: 75, status: 'healthy' }
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'warning':
        return <AlertCircle className="h-5 w-5 text-yellow-500" />;
      case 'critical':
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return <Activity className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
      case 'warning':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
      case 'critical':
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const getMetricColor = (current, threshold) => {
    const percentage = (current / threshold) * 100;
    if (percentage >= 90) return 'text-red-600';
    if (percentage >= 75) return 'text-yellow-600';
    return 'text-green-600';
  };

  const filteredSystems = selectedSystem === 'all' 
    ? healthData.systems 
    : healthData.systems.filter(system => system.status === selectedSystem);

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">System Health</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Monitor system performance and health status
          </p>
        </div>
        
        <div className="flex items-center space-x-2 mt-4 sm:mt-0">
          <select
            value={selectedSystem}
            onChange={(e) => setSelectedSystem(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
          >
            <option value="all">All Systems</option>
            <option value="healthy">Healthy</option>
            <option value="warning">Warning</option>
            <option value="critical">Critical</option>
          </select>
        </div>
      </div>

      {/* Overall Health Status */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center space-x-3 mb-4">
          <Heart className="h-6 w-6 text-green-500" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Overall System Health</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">3</div>
            <div className="text-sm text-gray-500 dark:text-gray-400">Healthy</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-yellow-600">1</div>
            <div className="text-sm text-gray-500 dark:text-gray-400">Warning</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-red-600">0</div>
            <div className="text-sm text-gray-500 dark:text-gray-400">Critical</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-600">99.2%</div>
            <div className="text-sm text-gray-500 dark:text-gray-400">Uptime</div>
          </div>
        </div>
      </div>

      {/* System Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {filteredSystems.map((system) => (
          <div
            key={system.id}
            className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 hover:shadow-lg transition-shadow duration-200"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="w-12 h-12 bg-brand-primary/10 rounded-lg flex items-center justify-center">
                <system.icon className="h-6 w-6 text-brand-primary" />
              </div>
              <div className="flex items-center space-x-2">
                {getStatusIcon(system.status)}
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(system.status)}`}>
                  {system.status}
                </span>
              </div>
            </div>
            
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
              {system.name}
            </h3>
            
            <div className="space-y-2 text-sm text-gray-500 dark:text-gray-400">
              <div className="flex justify-between">
                <span>Uptime:</span>
                <span className="font-medium text-gray-900 dark:text-white">{system.uptime}</span>
              </div>
              <div className="flex justify-between">
                <span>Response:</span>
                <span className="font-medium text-gray-900 dark:text-white">{system.responseTime}</span>
              </div>
              <div className="flex justify-between">
                <span>Last Check:</span>
                <span className="text-xs">{system.lastCheck}</span>
              </div>
            </div>
            
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button className="w-full px-3 py-2 text-sm font-medium text-brand-primary hover:bg-brand-primary/10 rounded-md transition-colors duration-200">
                View Details
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Resource Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Resource Usage</h3>
          <div className="space-y-4">
            {Object.entries(healthData.metrics).map(([key, metric]) => (
              <div key={key}>
                <div className="flex justify-between text-sm mb-2">
                  <span className="font-medium text-gray-700 dark:text-gray-300 capitalize">
                    {key === 'cpu' ? 'CPU' : key === 'memory' ? 'Memory' : key === 'disk' ? 'Disk' : 'Network'}
                  </span>
                  <span className={`font-semibold ${getMetricColor(metric.current, metric.threshold)}`}>
                    {metric.current}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all duration-300 ${
                      metric.current >= metric.threshold * 0.9 ? 'bg-red-500' :
                      metric.current >= metric.threshold * 0.75 ? 'bg-yellow-500' : 'bg-green-500'
                    }`}
                    style={{ width: `${(metric.current / metric.threshold) * 100}%` }}
                  ></div>
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Threshold: {metric.threshold}%
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Recent Alerts</h3>
          <div className="space-y-3">
            <div className="flex items-center space-x-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
              <AlertCircle className="h-5 w-5 text-yellow-500" />
              <div>
                <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                  Web Server Response Time High
                </p>
                <p className="text-xs text-yellow-600 dark:text-yellow-300">
                  2 minutes ago
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
              <CheckCircle className="h-5 w-5 text-green-500" />
              <div>
                <p className="text-sm font-medium text-green-800 dark:text-green-200">
                  Database Connection Restored
                </p>
                <p className="text-xs text-green-600 dark:text-green-300">
                  15 minutes ago
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* System Logs */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">System Logs</h3>
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 font-mono text-sm">
          <div className="text-green-600">[2024-01-15 14:30:15] INFO: All systems operational</div>
          <div className="text-yellow-600">[2024-01-15 14:28:42] WARN: Web server response time increased</div>
          <div className="text-blue-600">[2024-01-15 14:25:18] INFO: Database backup completed</div>
          <div className="text-green-600">[2024-01-15 14:20:33] INFO: Health check passed</div>
        </div>
      </div>
    </div>
  );
}

export default HealthPage;
